import { ClockIcon } from '@heroicons/react/24/outline';
import { useCharge, usePracticeOptions } from '../../../../hooks/useBilling';
import { useVendor } from '../../../../hooks/useVendors';
import { formatMoneyDollars } from '../../../../utils/money';
import { PatientLink } from '../../PatientLink';
import { SummaryLine } from '../../SummaryLine';
import { WizardSummaryPane } from '../../WizardSummaryPane';
import { fmtPoDate } from '../poFormat';
import type { CreatePurchaseOrderDraft } from './createPurchaseOrderDraft';

interface PurchaseOrderSummaryPaneProps {
    draft: CreatePurchaseOrderDraft;
}

// Mirrors CreateChargeModal's live summary: practice, patient and charge come
// from the selected charge, not chosen directly, so they fill in together.
export function PurchaseOrderSummaryPane({ draft }: PurchaseOrderSummaryPaneProps) {
    const { practices } = usePracticeOptions();
    const practice = practices.find((p) => p.id === draft.practiceId);
    const { charge } = useCharge(draft.chargeId ?? '');
    const { vendor } = useVendor(draft.vendorId);
    const orderMethod = vendor?.orderMethods.find((m) => m.id === draft.orderMethodId);

    const selectedLines = (charge?.lineItems ?? []).filter((line) =>
        draft.chargeLineItemIds.includes(line.id)
    );
    const expectedTotal = selectedLines.reduce((sum, line) => sum + Number(line.lineTotal), 0);

    return (
        <WizardSummaryPane>
            {practice && (
                <div className="flex flex-col gap-2">
                    <SummaryLine label="Practice" value={practice.name} />
                    {charge && (
                        <SummaryLine
                            label="Patient"
                            value={
                                charge.patientId ? (
                                    <PatientLink
                                        patientId={charge.patientId}
                                        patientName={charge.patientName}
                                    >
                                        {charge.patientName ?? '-'}
                                    </PatientLink>
                                ) : (
                                    'No patient'
                                )
                            }
                        />
                    )}
                    {charge && (
                        <SummaryLine
                            label="Charge"
                            value={charge.description || 'No description'}
                        />
                    )}
                </div>
            )}

            {selectedLines.length > 0 && (
                <div className="flex flex-col gap-2">
                    <SummaryLine label="Line items" value={`${selectedLines.length} selected`} />
                    <SummaryLine
                        label="Expected total"
                        value={formatMoneyDollars(expectedTotal)}
                        strong
                    />
                </div>
            )}

            {(vendor || orderMethod) && (
                <div className="flex flex-col gap-2">
                    {vendor && <SummaryLine label="Vendor" value={vendor.name} />}
                    {orderMethod && (
                        <SummaryLine label="Ordering method" value={orderMethod.name} />
                    )}
                </div>
            )}

            {draft.poNumber && <SummaryLine label="PO number" value={draft.poNumber} />}
            <SummaryLine label="Placed" value={fmtPoDate(draft.sentAt)} />

            <div className="flex items-start gap-2 text-[11px] text-gray-400 mt-auto pt-4">
                <ClockIcon className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span>
                    Records an order already placed on the vendor's site. This does not send an
                    email.
                </span>
            </div>
        </WizardSummaryPane>
    );
}
